import { isLargeOperation } from '@festina/configuration-general-types/configuration'
import { dateUtils, ReactUtils, type SetRequired } from '@festina-life/core'

import type {
  ConfiguredEnumLiteral,
  EnumDefinition,
} from '../../uiModels/enumLiterals/types'
import { partitionValueOperations } from '../../uiModels/parameterOperations/partitionValueOperations'
import type { ParameterValueOperation } from '../../uiModels/parameterOperations/types'
import { isDependencyValidationError } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { AnnulOperationsList } from './AnnulOperationsList'
import { EnumDefinitionOperationsWithErrors } from './EnumDefinitionOperationsWithErrors'
import { EnumLiteralOperationsWithErrors } from './EnumLiteralOperationsWithErrors'
import { ErrorsDisclosure } from './ErrorsDisclosure'
import { GlMapOperationsWithErrors } from './GlMapOperationsWithErrors'
import {
  configurationLayerItemsToList,
  filterEnumDefinitionErrors,
  filterEnumLiteralErrors,
  filterGlMapErrors,
  filterParameterDefinitionErrors,
  filterParameterValueAmendmentErrors,
  filterParameterValueErrors,
  filterReconciliationSpecErrors,
  filterTemplateErrors,
} from './helpers'
import { ParameterOperationsWithErrors } from './ParameterOperationsWithErrors'
import { ReconciliationSpecOperationsWithErrors } from './ReconciliationSpecOperationsWithErrors'
import { RegisteredTenantOperations } from './RegisteredTenantOperations'
import { TemplateOperationsWithErrors } from './TemplateOperationsWithErrors'
import type { OperationsListVariant } from './types'

const valueOperationRow = {
  parameterName: (operation: ParameterValueOperation) =>
    (isLargeOperation(operation)
      ? operation.innerOperationName
      : operation.entity?.name) ?? '',
  fromDate: (operation: ParameterValueOperation) =>
    isLargeOperation(operation) || !operation.entity?.fromDate
      ? undefined
      : dateUtils.dateUtcAsLocal(operation.entity.fromDate).toDate(),
  toDate: (operation: ParameterValueOperation) =>
    isLargeOperation(operation) || !operation.entity?.toDate
      ? undefined
      : dateUtils.dateUtcAsLocal(operation.entity.toDate).toDate(),
}

type Props = {
  block: string
  variant: OperationsListVariant
}

export const BlockScopedOperationsWithErrors = ({ block, variant }: Props) => {
  const {
    workspaceKey,
    templateOperations,
    configuredEnums,
    parameterDefinitionCreateOperations,
    parameterValueEntityOperations,
    registeredTenantCreateOperations,
    glMapOperations,
    reconciliationSpecCreateOperations,
    allAnnulOperationsByKey,
    errors: allErrors,
  } = useOperationsContext()
  const isWorkspace = !!workspaceKey

  const blockErrors = allErrors.filter(
    ({ block: errorBlock }) => errorBlock === block,
  )
  const locationTemplateOperations = configurationLayerItemsToList(
    templateOperations.get(block),
  )
    .flatMap((indexedTemplate) => indexedTemplate.operations)
    .filter((operation) =>
      isWorkspace ? operation.isDraft : !operation.isDraft,
    )

  const templateErrors = blockErrors.filter((error) => {
    const isTemplateError = filterTemplateErrors(error)
    const internalError = error.error
    return (
      isTemplateError ||
      (isDependencyValidationError(internalError) &&
        locationTemplateOperations.some(
          (indexedOperation) =>
            indexedOperation.internalOperation.operationKey ===
            internalError.operationKey,
        ))
    )
  })

  const locationConfiguredEnums = configurationLayerItemsToList(
    configuredEnums.get(block),
  )

  const allConfiguredEnumNames = locationConfiguredEnums.map(
    (configuredEnum) => configuredEnum.name,
  )

  const locationEnumLiterals = locationConfiguredEnums
    .flatMap((configuredEnum) => configuredEnum.literals)
    .filter(
      (operation): operation is ConfiguredEnumLiteral =>
        operation.type === 'configured' &&
        (isWorkspace ? operation.isDraft : !operation.isDraft),
    )

  const enumLiteralErrors = blockErrors.filter((error) =>
    filterEnumLiteralErrors(error, allConfiguredEnumNames),
  )

  const locationEnumDefinitions = locationConfiguredEnums
    .filter((operation) =>
      isWorkspace ? operation.isDraft : !operation.isDraft,
    )
    .filter(
      (
        definition,
      ): definition is SetRequired<EnumDefinition, 'internalOperation'> =>
        !!definition.internalOperation,
    )

  const enumDefinitionErrors = blockErrors.filter((error) =>
    filterEnumDefinitionErrors(error, allConfiguredEnumNames),
  )

  const locationParameterDefinitions = configurationLayerItemsToList(
    parameterDefinitionCreateOperations.get(block),
  )
    .flatMap((operation) => operation.operations)
    .filter((operation) =>
      isWorkspace ? operation.isDraft : !operation.isDraft,
    )

  const parameterDefinitionErrors = blockErrors.filter(
    filterParameterDefinitionErrors,
  )

  const locationParameterValues = configurationLayerItemsToList(
    parameterValueEntityOperations.get(block),
  )
    .flat()
    .filter((operation) =>
      isWorkspace ? operation.isDraft : !operation.isDraft,
    )

  const { creates: locationParameterValueCreates, amends: locationAmendments } =
    partitionValueOperations(locationParameterValues)

  const parameterValueErrors = blockErrors.filter(filterParameterValueErrors)
  const amendmentErrors = blockErrors.filter(
    filterParameterValueAmendmentErrors,
  )

  const locationRegisteredTenants = configurationLayerItemsToList(
    registeredTenantCreateOperations.get(block),
  )
    .flatMap((tenant) => tenant.operations)
    .filter(({ isDraft }) => (isWorkspace ? isDraft : !isDraft))

  const locationGlMaps = configurationLayerItemsToList(
    glMapOperations.get(block),
  )
    .flatMap((glMap) => glMap.operations)
    .filter(({ isDraft }) => (isWorkspace ? isDraft : !isDraft))

  const glMapErrors = blockErrors.filter(filterGlMapErrors)

  const locationReconciliationSpecs = configurationLayerItemsToList(
    reconciliationSpecCreateOperations.get(block),
  )
    .flatMap((reconciliationSpec) => reconciliationSpec.operations)
    .filter(({ isDraft }) => (isWorkspace ? isDraft : !isDraft))

  const reconciliationSpecErrors = blockErrors.filter(
    filterReconciliationSpecErrors,
  )

  const draftAnnulOperationsCount = Array.from(
    allAnnulOperationsByKey.values(),
  ).filter(
    ({ isDraft, block: operationBlock }) => isDraft && operationBlock === block,
  ).length

  const areOperationsPresent =
    !!locationTemplateOperations.length ||
    !!locationEnumLiterals.length ||
    !!locationEnumDefinitions.length ||
    !!locationParameterDefinitions.length ||
    !!locationParameterValues.length ||
    !!locationRegisteredTenants.length ||
    !!locationGlMaps.length ||
    !!locationReconciliationSpecs.length ||
    !!draftAnnulOperationsCount

  // Registered tenants currently don't have errors reported by the configuration server so we don't count them.
  const errorCount =
    templateErrors.length +
    enumLiteralErrors.length +
    enumDefinitionErrors.length +
    parameterDefinitionErrors.length +
    parameterValueErrors.length +
    amendmentErrors.length +
    glMapErrors.length +
    reconciliationSpecErrors.length

  const isShowingOperations =
    variant === 'errors' ? errorCount > 0 : areOperationsPresent

  return isShowingOperations ? (
    <ReactUtils.ConditionalWrap
      key={block}
      condition={variant === 'errors'}
      wrap={(children) => (
        <ErrorsDisclosure count={errorCount} title={block}>
          {children}
        </ErrorsDisclosure>
      )}
    >
      <>
        <TemplateOperationsWithErrors
          errors={templateErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          operations={locationTemplateOperations.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          variant={variant}
        />
        <ParameterOperationsWithErrors
          operations={locationParameterDefinitions.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          errors={parameterDefinitionErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          operationType="parameterDefinition"
          parameterName={(operation) => operation.entity?.name ?? ''}
          fromDate={(operation) =>
            operation.entity?.fromDate
              ? dateUtils.dateUtcAsLocal(operation.entity.fromDate).toDate()
              : undefined
          }
          variant={variant}
        />
        <ParameterOperationsWithErrors
          operations={locationParameterValueCreates.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          errors={parameterValueErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          operationType="parameterValue"
          {...valueOperationRow}
          variant={variant}
        />
        <ParameterOperationsWithErrors
          operations={locationAmendments.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          errors={amendmentErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          operationType="parameterValueAmendment"
          {...valueOperationRow}
          variant={variant}
        />
        <EnumDefinitionOperationsWithErrors
          operations={locationEnumDefinitions.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          errors={enumDefinitionErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          variant={variant}
        />
        <EnumLiteralOperationsWithErrors
          operations={locationEnumLiterals.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          errors={enumLiteralErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          variant={variant}
        />
        <RegisteredTenantOperations
          operations={locationRegisteredTenants.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          variant={variant}
        />
        <GlMapOperationsWithErrors
          operations={locationGlMaps.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          errors={glMapErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          variant={variant}
        />
        <ReconciliationSpecOperationsWithErrors
          operations={locationReconciliationSpecs.filter(
            ({ block: operationBlock }) => operationBlock === block,
          )}
          errors={reconciliationSpecErrors.filter(
            ({ block: errorBlock }) => errorBlock === block,
          )}
          variant={variant}
        />
        <AnnulOperationsList block={block} variant={variant} />
      </>
    </ReactUtils.ConditionalWrap>
  ) : null
}
